import { supabase } from '../lib/supabase';

/**
 * Email Notification Service
 * Sends branded email notifications via Resend integration
 */

/**
 * Get user email preferences
 */
export async function getEmailPreferences(userId) {
  try {
    const { data, error } = await supabase
      ?.from('email_preferences')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.single();

    if (error) {
      // If no preferences exist, create default ones
      if (error?.code === 'PGRST116') {
        const { data: newPrefs, error: createError } = await supabase
          ?.from('email_preferences')
          ?.insert({ user_id: userId })
          ?.select()
          ?.single();

        if (createError) throw createError;
        return convertToEmailPreferences(newPrefs);
      }
      throw error;
    }

    return convertToEmailPreferences(data);
  } catch (error) {
    console.error('Error fetching email preferences:', error);
    throw error;
  }
}

/**
 * Update user email preferences
 */
export async function updateEmailPreferences(userId, preferences) {
  try {
    const { data, error } = await supabase
      ?.from('email_preferences')
      ?.update({
        brand_mention_alerts: preferences?.brandMentionAlerts,
        performance_reports: preferences?.performanceReports,
        campaign_notifications: preferences?.campaignNotifications,
        threat_alerts: preferences?.threatAlerts,
        trend_alerts: preferences?.trendAlerts,
        weekly_digest: preferences?.weeklyDigest,
        monthly_report: preferences?.monthlyReport,
        email_frequency: preferences?.emailFrequency,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;
    return convertToEmailPreferences(data);
  } catch (error) {
    console.error('Error updating email preferences:', error);
    throw error;
  }
}

/**
 * Get email template by name
 */
export async function getEmailTemplate(templateName) {
  try {
    const { data, error } = await supabase
      ?.from('email_templates')
      ?.select('*')
      ?.eq('template_name', templateName)
      ?.eq('is_active', true)
      ?.single();

    if (error) throw error;
    return convertToEmailTemplate(data);
  } catch (error) {
    console.error('Error fetching email template:', error);
    throw error;
  }
}

/**
 * Render email template with data
 */
function renderTemplate(template, data) {
  let subject = template?.subjectTemplate;
  let html = template?.htmlTemplate;
  let text = template?.textTemplate;

  // Replace all template variables
  Object?.keys(data)?.forEach(key => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    const value = data?.[key] || '';
    subject = subject?.replace(regex, value);
    html = html?.replace(regex, value);
    if (text) {
      text = text?.replace(regex, value);
    }
  });

  return { subject, html, text };
}

/**
 * Send email via Resend Edge Function
 */
async function sendViaResend(to, subject, html, text) {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: { to, subject, html, text }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error sending email via Resend:', error);
    throw error;
  }
}

/**
 * Log email notification to database
 */
async function logEmailNotification({
  userId,
  brandId,
  notificationType,
  recipientEmail,
  subject,
  templateName,
  templateData,
  status,
  resendId,
  errorMessage,
  relatedEntityId,
  relatedEntityType
}) {
  try {
    const { data, error } = await supabase
      ?.from('email_notifications')
      ?.insert({
        user_id: userId,
        brand_id: brandId,
        notification_type: notificationType,
        recipient_email: recipientEmail,
        subject: subject,
        template_name: templateName,
        template_data: templateData,
        status: status,
        resend_id: resendId,
        error_message: errorMessage,
        sent_at: status === 'sent' ? new Date()?.toISOString() : null,
        related_entity_id: relatedEntityId,
        related_entity_type: relatedEntityType
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error logging email notification:', error);
    throw error;
  }
}

/**
 * Send brand mention alert email
 */
export async function sendBrandMentionAlert(mention, userEmail, userName) {
  try {
    // Get email preferences
    const preferences = await getEmailPreferences(mention?.userId);
    if (!preferences?.brandMentionAlerts) {
      console.log('Brand mention alerts disabled for user');
      return null;
    }

    // Get template
    const template = await getEmailTemplate('brand_mention_alert');

    // Prepare template data
    const templateData = {
      userName: userName,
      brandName: mention?.brandName,
      sentiment: mention?.sentiment,
      platform: mention?.platform,
      authorName: mention?.authorName,
      authorHandle: mention?.authorHandle,
      content: mention?.content,
      reach: mention?.reach?.toLocaleString(),
      engagement: mention?.engagementCount?.toLocaleString(),
      sentimentScore: mention?.sentimentScore,
      dashboardUrl: `${window?.location?.origin}/brand-monitoring-center`
    };

    // Render template
    const { subject, html, text } = renderTemplate(template, templateData);

    // Send email
    const result = await sendViaResend(userEmail, subject, html, text);

    // Log notification
    await logEmailNotification({
      userId: mention?.userId,
      brandId: mention?.brandId,
      notificationType: 'brand_mention_alert',
      recipientEmail: userEmail,
      subject: subject,
      templateName: 'brand_mention_alert',
      templateData: templateData,
      status: 'sent',
      resendId: result?.id,
      relatedEntityId: mention?.id,
      relatedEntityType: 'brand_mention'
    });

    return result;
  } catch (error) {
    console.error('Error sending brand mention alert:', error);
    
    // Log failed notification
    await logEmailNotification({
      userId: mention?.userId,
      brandId: mention?.brandId,
      notificationType: 'brand_mention_alert',
      recipientEmail: userEmail,
      subject: 'Brand Mention Alert',
      templateName: 'brand_mention_alert',
      templateData: {},
      status: 'failed',
      errorMessage: error?.message,
      relatedEntityId: mention?.id,
      relatedEntityType: 'brand_mention'
    });

    throw error;
  }
}

/**
 * Send performance report email
 */
export async function sendPerformanceReport({
  userId,
  brandId,
  brandName,
  userEmail,
  userName,
  reportType,
  metrics
}) {
  try {
    // Get email preferences
    const preferences = await getEmailPreferences(userId);
    if (!preferences?.performanceReports) {
      console.log('Performance reports disabled for user');
      return null;
    }

    // Get template
    const template = await getEmailTemplate('performance_report');

    // Prepare template data
    const templateData = {
      userName: userName,
      brandName: brandName,
      reportType: reportType,
      totalReach: metrics?.totalReach?.toLocaleString(),
      totalEngagement: metrics?.totalEngagement?.toLocaleString(),
      engagementRate: metrics?.engagementRate?.toFixed(2),
      postsPublished: metrics?.postsPublished,
      dashboardUrl: `${window?.location?.origin}/analytics-dashboard`
    };

    // Render template
    const { subject, html, text } = renderTemplate(template, templateData);

    // Send email
    const result = await sendViaResend(userEmail, subject, html, text);

    // Log notification
    await logEmailNotification({
      userId: userId,
      brandId: brandId,
      notificationType: 'performance_report',
      recipientEmail: userEmail,
      subject: subject,
      templateName: 'performance_report',
      templateData: templateData,
      status: 'sent',
      resendId: result?.id,
      relatedEntityId: brandId,
      relatedEntityType: 'brand'
    });

    return result;
  } catch (error) {
    console.error('Error sending performance report:', error);
    
    // Log failed notification
    await logEmailNotification({
      userId: userId,
      brandId: brandId,
      notificationType: 'performance_report',
      recipientEmail: userEmail,
      subject: 'Performance Report',
      templateName: 'performance_report',
      templateData: {},
      status: 'failed',
      errorMessage: error?.message,
      relatedEntityId: brandId,
      relatedEntityType: 'brand'
    });

    throw error;
  }
}

/**
 * Send campaign notification email
 */
export async function sendCampaignNotification(campaign, userEmail, userName) {
  try {
    // Get email preferences
    const preferences = await getEmailPreferences(campaign?.userId);
    if (!preferences?.campaignNotifications) {
      console.log('Campaign notifications disabled for user');
      return null;
    }

    // Get template
    const template = await getEmailTemplate('campaign_notification');

    // Prepare template data
    const templateData = {
      userName: userName,
      campaignName: campaign?.name,
      brandName: campaign?.brandName,
      status: campaign?.status,
      startDate: new Date(campaign?.startDate)?.toLocaleDateString(),
      endDate: new Date(campaign?.endDate)?.toLocaleDateString(),
      goalProgress: campaign?.goalProgress,
      campaignUrl: `${window?.location?.origin}/campaign-management`
    };

    // Render template
    const { subject, html, text } = renderTemplate(template, templateData);

    // Send email
    const result = await sendViaResend(userEmail, subject, html, text);

    // Log notification
    await logEmailNotification({
      userId: campaign?.userId,
      brandId: campaign?.brandId,
      notificationType: 'campaign_notification',
      recipientEmail: userEmail,
      subject: subject,
      templateName: 'campaign_notification',
      templateData: templateData,
      status: 'sent',
      resendId: result?.id,
      relatedEntityId: campaign?.id,
      relatedEntityType: 'campaign'
    });

    return result;
  } catch (error) {
    console.error('Error sending campaign notification:', error);
    
    // Log failed notification
    await logEmailNotification({
      userId: campaign?.userId,
      brandId: campaign?.brandId,
      notificationType: 'campaign_notification',
      recipientEmail: userEmail,
      subject: 'Campaign Notification',
      templateName: 'campaign_notification',
      templateData: {},
      status: 'failed',
      errorMessage: error?.message,
      relatedEntityId: campaign?.id,
      relatedEntityType: 'campaign'
    });

    throw error;
  }
}

/**
 * Get email notification history
 */
export async function getEmailNotificationHistory(userId, filters = {}) {
  try {
    let query = supabase
      ?.from('email_notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (filters?.notificationType) {
      query = query?.eq('notification_type', filters?.notificationType);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(notification => ({
      id: notification?.id,
      userId: notification?.user_id,
      brandId: notification?.brand_id,
      notificationType: notification?.notification_type,
      recipientEmail: notification?.recipient_email,
      subject: notification?.subject,
      templateName: notification?.template_name,
      status: notification?.status,
      resendId: notification?.resend_id,
      errorMessage: notification?.error_message,
      sentAt: notification?.sent_at,
      deliveredAt: notification?.delivered_at,
      relatedEntityId: notification?.related_entity_id,
      relatedEntityType: notification?.related_entity_type,
      createdAt: notification?.created_at
    })) || [];
  } catch (error) {
    console.error('Error fetching email notification history:', error);
    throw error;
  }
}

// Helper conversion functions
function convertToEmailPreferences(data) {
  return {
    id: data?.id,
    userId: data?.user_id,
    brandMentionAlerts: data?.brand_mention_alerts,
    performanceReports: data?.performance_reports,
    campaignNotifications: data?.campaign_notifications,
    threatAlerts: data?.threat_alerts,
    trendAlerts: data?.trend_alerts,
    weeklyDigest: data?.weekly_digest,
    monthlyReport: data?.monthly_report,
    emailFrequency: data?.email_frequency,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  };
}

function convertToEmailTemplate(data) {
  return {
    id: data?.id,
    templateName: data?.template_name,
    notificationType: data?.notification_type,
    subjectTemplate: data?.subject_template,
    htmlTemplate: data?.html_template,
    textTemplate: data?.text_template,
    variables: data?.variables,
    isActive: data?.is_active,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  };
}